import { useNavigate } from 'react-router-dom';

import { BatchListItem } from '@e-origin/contracts';
import { BatchTypeEnum, DeclarationStatus, FilterRisksValues } from '@e-origin/shared';

import { StatisticLink, TableColumnProps } from '@components';
import { FormatValueTypes, RenderAnalysisParams } from '@enums';
import { formatValue } from '@utils';
import { renderAnalysisWrongParamsFixed } from './helper';

const ADDITIONAL_COLUMNS_STYLES = {
  backgroundColor: '#F4F9FF',
};

export const useBuildRiskAnalysisColumns = ({ settings, batchView }): TableColumnProps<BatchListItem>[] => {
  const navigate = useNavigate();

  const handleStatisticsClick = (row: BatchListItem, tab: string, filter?: FilterRisksValues) => {
    if (row.status.value !== 'failed') {
      let path = `/declarations?batchId=${row._id}&tab=${tab}`;
      if (filter) {
        path = `${path}&riskValues=${filter}`;
      }
      navigate(path);
    }
  };

  return [BatchTypeEnum.HIGH_VALUES, BatchTypeEnum.HIGH_VALUES_EXPORT].includes(batchView)
    ? [
        {
          name: 'Impact %',
          selector: 'riskAnalysis.impactInPercentage',
          sortable: true,
          style: ADDITIONAL_COLUMNS_STYLES,
          cell: (row) => (
            <StatisticLink
              data-pw={`cell-impact-${row.name}`}
              disabled={row.status.value === 'failed'}
              onClick={() => {
                navigate(`/declaration-groups?batchId=${row._id}`);
              }}
            >
              {formatValue(row.riskAnalysis?.impactInPercentage || 0, FormatValueTypes.PERCENTAGE)}
            </StatisticLink>
          ),
        },
        {
          name: 'Minimum amount €',
          selector: 'riskAnalysis.impactInEuro',
          sortable: true,
          style: ADDITIONAL_COLUMNS_STYLES,
          width: '100px',
          cell: (row) => (
            <StatisticLink
              disabled={row.status.value === 'failed'}
              onClick={() => {
                navigate(`/declaration-groups?batchId=${row._id}`);
              }}
            >
              {formatValue(row.riskAnalysis?.impactInEuro || 0, FormatValueTypes.PERCENTAGE)}
            </StatisticLink>
          ),
        },
        {
          name: 'Value',
          sortable: true,
          selector: 'statistics.analysisWrongValue',
          style: ADDITIONAL_COLUMNS_STYLES,
          cell: (row) => (
            <StatisticLink
              disabled={row.status.value === 'failed'}
              onClick={() => {
                handleStatisticsClick(row, DeclarationStatus.ANALYSIS, FilterRisksValues.INVOICED_RATE_WEB_OK);
              }}
            >
              {renderAnalysisWrongParamsFixed(row, RenderAnalysisParams.VALUE)}
            </StatisticLink>
          ),
        },
        {
          name: 'Desc.',
          selector: 'statistics.analysisWrongDescription',
          sortable: true,
          style: ADDITIONAL_COLUMNS_STYLES,
          cell: (row) => (
            <StatisticLink
              disabled={row.status.value === 'failed'}
              onClick={() => {
                handleStatisticsClick(row, DeclarationStatus.ANALYSIS, FilterRisksValues.WEB_MATCHING_DESCRIPTION);
              }}
            >
              {renderAnalysisWrongParamsFixed(row, RenderAnalysisParams.DESCRIPTION)}
            </StatisticLink>
          ),
        },
        {
          name: 'HS code',
          selector: 'statistics.analysisWrongHsCode',
          sortable: true,
          style: ADDITIONAL_COLUMNS_STYLES,
          cell: (row) => (
            <StatisticLink
              disabled={row.status.value === 'failed'}
              onClick={() => {
                handleStatisticsClick(row, DeclarationStatus.ANALYSIS, FilterRisksValues.HS_DESCRIPTION_OK);
              }}
            >
              {renderAnalysisWrongParamsFixed(row, RenderAnalysisParams.HS_CODE)}
            </StatisticLink>
          ),
        },
      ]
    : [
        {
          name: 'Split Shipment',
          selector: 'statistics.analysisSplitShipment',
          sortable: true,
          style: ADDITIONAL_COLUMNS_STYLES,
          cell: (row) => (
            <StatisticLink
              disabled={row.status.value === 'failed'}
              onClick={() => {
                handleStatisticsClick(row, DeclarationStatus.NOT_SENT, FilterRisksValues.SPLIT_SHIPMENT);
              }}
            >
              {row.statistics?.analysisSplitShipment}
            </StatisticLink>
          ),
        },
        ...(settings?.walletEnabled
          ? [
              {
                name: 'Wrong IOSS',
                selector: 'statistics.analysisIOSS',
                sortable: true,
                style: ADDITIONAL_COLUMNS_STYLES,
                cell: (row) => (
                  <StatisticLink
                    disabled={row.status.value === 'failed'}
                    onClick={() => {
                      handleStatisticsClick(row, DeclarationStatus.NOT_SENT, FilterRisksValues.WRONG_IOSS);
                    }}
                  >
                    {row.statistics?.analysisIOSS}
                  </StatisticLink>
                ),
              },
              {
                name: 'Undervaluation',

                sortable: true,
                selector: 'statistics.analysisUndervaluation',
                style: ADDITIONAL_COLUMNS_STYLES,
                cell: (row) => (
                  <StatisticLink
                    disabled={row.status.value === 'failed'}
                    onClick={() => {
                      handleStatisticsClick(row, DeclarationStatus.NOT_SENT, FilterRisksValues.UNDERVALUATION);
                    }}
                  >
                    {row.statistics?.analysisUndervaluation}
                  </StatisticLink>
                ),
              },
            ]
          : []),
      ];
};
